import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, ArrowLeft, AlertTriangle, CheckCircle, XCircle,
  RefreshCw, Zap, TrendingDown, WifiOff, Wrench
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHealthColor = (status) => {
  switch(status) {
    case 'excellent': return 'text-green-600 bg-green-50 border-green-200';
    case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'fair': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'poor': return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'critical': return 'text-red-600 bg-red-50 border-red-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const getSeverityColor = (severity) => {
  if (severity === 'critical') return 'bg-red-100 text-red-800 border-red-300';
  if (severity === 'warning') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-blue-100 text-blue-800 border-blue-300';
};

const NetworkDiagnostics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [healthSummary, setHealthSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [runningDiagnostics, setRunningDiagnostics] = useState(false);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerHistory(selectedCustomer.customer_id);
    }
  }, [selectedCustomer]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');

      // Fetch health summary
      const summaryResponse = await fetch(`${API_URL}/api/monitoring/diagnostics/health-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (summaryResponse.ok) {
        const data = await summaryResponse.json();
        setHealthSummary(data.summary);
      }

      // Fetch alerts
      const alertsResponse = await fetch(`${API_URL}/api/monitoring/diagnostics/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (alertsResponse.ok) {
        const data = await alertsResponse.json();
        setAlerts(data.alerts || []);
      }

      // Fetch customers with latest diagnostics
      const customersResponse = await fetch(`${API_URL}/api/customer-monitoring/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (customersResponse.ok) {
        const data = await customersResponse.json();
        setCustomers(data || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load diagnostics data');
      setLoading(false);
    }
  };

  const fetchCustomerHistory = async (customerId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/monitoring/diagnostics/customer/${customerId}?hours=24`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const formattedData = data.history.map(h => ({
          time: new Date(h.timestamp).toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false
          }),
          health_score: h.health_score,
          latency: h.quality_test?.latency_ms || 0,
          packet_loss: h.quality_test?.packet_loss_percent || 0,
          bandwidth_usage: h.bandwidth_usage?.usage_percent || 0
        })).reverse();

        setCustomerHistory(formattedData);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
    }
  };

  const runDiagnostics = async () => {
    try {
      setRunningDiagnostics(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/diagnostics/run`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Diagnostics completed for ${data.diagnosed} customers`);
        await fetchData();
      } else {
        toast.error('Failed to run diagnostics');
      }
    } catch (error) {
      toast.error('Error running diagnostics');
    } finally {
      setRunningDiagnostics(false);
    }
  };

  const triggerAutoRemediation = async (customerId, issueType) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/monitoring/diagnostics/auto-remediate/${customerId}?issue_type=${issueType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(data.result.message || 'Auto-remediation triggered successfully');
        } else {
          toast.error(data.result.message || 'Auto-remediation failed');
        }
      }
    } catch (error) {
      toast.error('Error triggering auto-remediation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading diagnostics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      {/* Header */}
      <div className="mb-6 bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Zap className="h-8 w-8" />
                Network Diagnostics
              </h1>
              <p className="text-orange-100 mt-1">Automated network health monitoring & alerts</p>
            </div>
          </div>
          <Button
            onClick={runDiagnostics}
            disabled={runningDiagnostics}
            className="bg-white text-red-600 hover:bg-red-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${runningDiagnostics ? 'animate-spin' : ''}`} />
            {runningDiagnostics ? 'Running...' : 'Run Diagnostics'}
          </Button>
        </div>
      </div>

      {/* Health Summary Cards */}
      {healthSummary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="text-sm opacity-90">Avg Health Score</div>
              <div className="text-3xl font-bold">{healthSummary.avg_health_score}/100</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Excellent</div>
              <div className="text-2xl font-bold text-green-600">{healthSummary.excellent_count}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Good</div>
              <div className="text-2xl font-bold text-blue-600">{healthSummary.good_count}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Fair/Poor</div>
              <div className="text-2xl font-bold text-orange-600">
                {healthSummary.fair_count + healthSummary.poor_count}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Critical</div>
              <div className="text-2xl font-bold text-red-600">{healthSummary.critical_count}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Alerts */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Active Alerts ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {alerts.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No active alerts</p>
                  </div>
                ) : (
                  alerts.map((alert, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-sm">{alert.customer_name}</div>
                        <span className="text-xs font-bold uppercase">{alert.severity}</span>
                      </div>
                      <div className="text-xs mb-2">{alert.message}</div>
                      {alert.severity === 'critical' && (
                        <Button
                          size="sm"
                          onClick={() => triggerAutoRemediation(alert.customer_id, alert.issue_type)}
                          className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white"
                        >
                          <Wrench className="h-3 w-3 mr-1" />
                          Auto-Fix
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Health Status & History */}
        <div className="lg:col-span-2">
          <Card className="mb-4">
            <CardHeader>
              <CardTitle>Customer Health Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {customers.map((customer) => {
                  // Mock health status for now
                  const health_status = customer.status === 'active' ? 'good' : 'critical';
                  return (
                    <div
                      key={customer.id}
                      onClick={() => setSelectedCustomer({ customer_id: customer.id, customer_name: customer.customer_name })}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedCustomer?.customer_id === customer.id
                          ? 'border-blue-500 bg-blue-50'
                          : getHealthColor(health_status)
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{customer.customer_name}</div>
                          <div className="text-xs opacity-75">{customer.static_ip}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs uppercase font-bold">{health_status}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Customer History Chart */}
          {selectedCustomer && (
            <Card>
              <CardHeader>
                <CardTitle>{selectedCustomer.customer_name} - 24 Hour History</CardTitle>
              </CardHeader>
              <CardContent>
                {customerHistory.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No diagnostic history available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Health Score</h4>
                      <ResponsiveContainer width="100%" height={120}>
                        <LineChart data={customerHistory}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{fontSize: 10}} />
                          <YAxis domain={[0, 100]} tick={{fontSize: 10}} />
                          <Tooltip />
                          <Line type="monotone" dataKey="health_score" stroke="#8b5cf6" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2">Latency (ms)</h4>
                      <ResponsiveContainer width="100%" height={100}>
                        <LineChart data={customerHistory}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{fontSize: 10}} />
                          <YAxis tick={{fontSize: 10}} />
                          <Tooltip />
                          <Line type="monotone" dataKey="latency" stroke="#3b82f6" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2">Bandwidth Usage (%)</h4>
                      <ResponsiveContainer width="100%" height={100}>
                        <LineChart data={customerHistory}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{fontSize: 10}} />
                          <YAxis domain={[0, 100]} tick={{fontSize: 10}} />
                          <Tooltip />
                          <Line type="monotone" dataKey="bandwidth_usage" stroke="#10b981" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkDiagnostics;
